import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Typography,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import axios from 'axios';

const MachineForm = ({ machine, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    location: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    opcua_endpoint_url: '',
    opcua_security_policy: 'None',
    opcua_security_mode: 'None',
    opcua_username: '',
    opcua_password: '',
    max_speed: '',
    status: 'inactive'
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (machine) {
      setFormData({
        name: machine.name || '',
        code: machine.code || '',
        description: machine.description || '',
        location: machine.location || '',
        manufacturer: machine.manufacturer || '',
        model: machine.model || '',
        serial_number: machine.serial_number || '',
        opcua_endpoint_url: machine.opcua_endpoint_url || '',
        opcua_security_policy: machine.opcua_security_policy || 'None',
        opcua_security_mode: machine.opcua_security_mode || 'None',
        opcua_username: machine.opcua_username || '',
        opcua_password: machine.opcua_password || '',
        max_speed: machine.max_speed || '',
        status: machine.status || 'inactive'
      });
    }
  }, [machine]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome macchina obbligatorio';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Codice macchina obbligatorio';
    }

    if (!formData.opcua_endpoint_url.trim()) {
      newErrors.opcua_endpoint_url = 'Endpoint OPC UA obbligatorio';
    } else if (!formData.opcua_endpoint_url.startsWith('opc.tcp://')) {
      newErrors.opcua_endpoint_url = 'Endpoint deve iniziare con opc.tcp://';
    }

    if (formData.max_speed && (isNaN(formData.max_speed) || parseInt(formData.max_speed) < 0)) {
      newErrors.max_speed = 'Velocità massima deve essere un numero positivo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        ...formData,
        max_speed: formData.max_speed ? parseInt(formData.max_speed) : null,
        // Only send password if it's been changed (for edit)
        opcua_password: formData.opcua_password || undefined
      };

      if (machine) {
        // Update existing machine
        await axios.put(`/api/machines/${machine.id}`, payload);
      } else {
        // Create new machine
        await axios.post('/api/machines', payload);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving machine:', error);
      setSubmitError(
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.msg ||
        'Errore nel salvataggio della macchina'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        Informazioni Generali
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Nome Macchina"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Codice"
            name="code"
            value={formData.code}
            onChange={handleChange}
            error={!!errors.code}
            helperText={errors.code}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descrizione"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={2}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Ubicazione"
            name="location"
            value={formData.location}
            onChange={handleChange}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Stato</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              label="Stato"
              disabled={submitting}
            >
              <MenuItem value="active">Attiva</MenuItem>
              <MenuItem value="inactive">Inattiva</MenuItem>
              <MenuItem value="maintenance">Manutenzione</MenuItem>
              <MenuItem value="error">Errore</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Produttore"
            name="manufacturer"
            value={formData.manufacturer}
            onChange={handleChange}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Modello"
            name="model"
            value={formData.model}
            onChange={handleChange}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Numero Seriale"
            name="serial_number"
            value={formData.serial_number}
            onChange={handleChange}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Velocità Massima (pz/min)"
            name="max_speed"
            type="number"
            value={formData.max_speed}
            onChange={handleChange}
            error={!!errors.max_speed}
            helperText={errors.max_speed}
            disabled={submitting}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Configurazione OPC UA
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Endpoint URL"
            name="opcua_endpoint_url"
            value={formData.opcua_endpoint_url}
            onChange={handleChange}
            error={!!errors.opcua_endpoint_url}
            helperText={errors.opcua_endpoint_url || 'Esempio: opc.tcp://192.168.1.100:4840'}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Security Policy</InputLabel>
            <Select
              name="opcua_security_policy"
              value={formData.opcua_security_policy}
              onChange={handleChange}
              label="Security Policy"
              disabled={submitting}
            >
              <MenuItem value="None">None</MenuItem>
              <MenuItem value="Basic128Rsa15">Basic128Rsa15</MenuItem>
              <MenuItem value="Basic256">Basic256</MenuItem>
              <MenuItem value="Basic256Sha256">Basic256Sha256</MenuItem>
            </Select>
            <FormHelperText>Politica di sicurezza OPC UA</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Security Mode</InputLabel>
            <Select
              name="opcua_security_mode"
              value={formData.opcua_security_mode}
              onChange={handleChange}
              label="Security Mode"
              disabled={submitting}
            >
              <MenuItem value="None">None</MenuItem>
              <MenuItem value="Sign">Sign</MenuItem>
              <MenuItem value="SignAndEncrypt">Sign And Encrypt</MenuItem>
            </Select>
            <FormHelperText>Modalità di sicurezza OPC UA</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Username OPC UA"
            name="opcua_username"
            value={formData.opcua_username}
            onChange={handleChange}
            disabled={submitting}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Password OPC UA"
            name="opcua_password"
            type="password"
            value={formData.opcua_password}
            onChange={handleChange}
            helperText={machine ? 'Lascia vuoto per mantenere la password attuale' : ''}
            disabled={submitting}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel} disabled={submitting}>
          Annulla
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? 'Salvataggio...' : (machine ? 'Aggiorna' : 'Crea')}
        </Button>
      </Box>
    </Box>
  );
};

export default MachineForm;
